import { PrinterCheck } from "lucide-react"
import React, { useRef, useState } from "react"
import { Card, Col, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { CardBody, CardTitle } from "reactstrap"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import axios from "axios"
import { API_URL, formatDateForAPI } from "../../../components/Common/Utils"
import { showAlert } from "../../../store/reducers/alertReducer"
import IconifyIcon from "../../../components/Shared/IconifyIcon"
import { layoutModeTypes } from "../../../constants/layout"
import Colors from "../../../constants/Colors"
import { useSelector } from "react-redux"
import CustomTabs from "../TheoreticalTraining/CustomTabs"
import goal16Icon from "../../../assets/images/icons/octicon_goal-24.svg"
import moment from "moment"

const TrialOperationsGoalCard = ({ title, data }) => {
  return (
    <Card className="mb-4 h-100 p-2 pb-0">
      <Card.Header className="position-header text-white text-center py-3">
        {title}
      </Card.Header>
      <Card.Body className="p-0">
        {data.map((item, index) => (
          <Row
            key={index}
            className={`mx-0 py-2 px-3 ${
              index < data.length - 1 ? "border-bottom" : ""
            }`}
          >
            <div className="g_between algn-items-start px-0">
              <h6 className="fw-bold">{item.label}</h6>
              <span className="fw-semibold">{item.value}</span>
            </div>
          </Row>
        ))}
      </Card.Body>
    </Card>
  )
}

const TrialOperationsGoalsDashboardV2 = ({ dashboardData, filters }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const layoutMode = useSelector(state => state.Layout.layoutModeType)

  const [activeTab, setActiveTab] = useState("SUMMARY")
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const pdfRef = useRef()
  const today = moment().format("DD-MM-YYYY")

  const toggleTab = tab => {
    setActiveTab(tab)
  }

  const headers = [
    { label: t("Shift"), key: "shift" },
    { label: t("Station"), key: "station" },
    { label: t("Enrolled"), key: "enrolled" },
    { label: t("Present"), key: "present" },
    { label: t("Absent"), key: "absent" },
    { label: t("Completed"), key: "completed" },
    { label: t("Present") + " %", key: "present_percentage" },
    { label: t("Absent") + " %", key: "absent_percentage" },
    { label: t("Completed") + " %", key: "completed_percentage" },
    { label: t("Catering"), key: "catering" },
  ]

  const safePercentage = (numerator, denominator) => {
    const num = Number(numerator) || 0 // Treat invalid values as 0
    const denom = Number(denominator) || 0
    return denom === 0 ? 0 : (num / denom) * 100
  }

  const tabularData = dashboardData?.summery?.map(summary => ({
    shift:
      language === "sa"
        ? summary.shift_details.name_ar
        : summary.shift_details.name,
    station: language === "sa" ? summary.station.name_ar : summary.station.name,
    enrolled: summary.total_enrolled,
    present: summary.total_present,
    absent: summary.total_absent,
    completed: summary.total_complete,
    present_percentage: safePercentage(
      summary.total_present,
      summary.total_enrolled
    ),
    absent_percentage: safePercentage(
      summary.total_absent,
      summary.total_enrolled
    ),
    completed_percentage: safePercentage(
      summary.total_complete,
      summary.total_enrolled
    ),
    catering: parseInt(summary.catering),
  }))

  // Calculate Totals
  let totals = headers.reduce((acc, header) => {
    const rowToSkip = [
      "shift",
      "station",
      "present_percentage",
      "absent_percentage",
    ]

    if (!rowToSkip.includes(header.key)) {
      acc[header.key] = tabularData?.reduce(
        (sum, row) => sum + (row[header.key] || 0),
        0
      )
    }

    return acc
  }, {})

  headers.reduce((acc, header) => {
    if (header.key === "present_percentage") {
      const enrolled = +totals["enrolled"]
      const present = +totals["present"]
      if (
        enrolled === 0 ||
        present === 0 ||
        isNaN(enrolled) ||
        isNaN(present)
      ) {
        totals[header.key] = 0
      } else {
        totals[header.key] = (present / enrolled) * 100
      }
    }

    if (header.key === "absent_percentage") {
      totals[header.key] =
        (totals["absent"] / (totals["enrolled"] || Infinity)) * 100 || 0
      const enrolled = +totals["enrolled"]
      const absent = +totals["absent"]
      if (enrolled === 0 || absent === 0 || isNaN(enrolled) || isNaN(absent)) {
        totals[header.key] = 0
      } else {
        totals[header.key] = (absent / enrolled) * 100
      }
    }

    if (header.key === "completed_percentage") {
      const enrolled = +totals["enrolled"]
      const completed = +totals["completed"]
      if (
        enrolled === 0 ||
        completed === 0 ||
        isNaN(enrolled) ||
        isNaN(completed)
      ) {
        totals[header.key] = 0
      } else {
        totals[header.key] = (completed / enrolled) * 100
      }
    }
    if (header.key === "new_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["new_candidates_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }
    if (header.key === "saudi_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["saudi_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }
    if (header.key === "non_saudi_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["non_saudi_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }

    return acc
  }, {})

  const formatNumber = num => {
    if (typeof num === "number" && !Number.isInteger(num)) {
      return num.toFixed(2)
    }
    return num
  }

  const exportAsPDF = async () => {
    setIsExporting(true)

    try {
      const pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const margin = 10 // margin in mm
      const contentWidth = pageWidth - margin * 2

      // Get all the page sections
      const pageSections = pdfRef.current.querySelectorAll(
        '[data-page-section="true"]'
      )

      // For each section
      for (let i = 0; i < pageSections.length; i++) {
        const section = pageSections[i]

        // Add new page if not the first section
        if (i > 0) {
          pdf.addPage()
        }

        // Convert this section to canvas
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        })

        const imgData = canvas.toDataURL("image/png")

        // Calculate dimensions while preserving aspect ratio
        const imgWidth = contentWidth
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        // If this section fits on one page
        if (imgHeight <= pageHeight - margin * 2) {
          // Add image centered on the page
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight)
        }
        // If this section needs multiple pages
        else {
          let heightLeft = imgHeight
          let position = 0
          let pageCount = 0

          // Add first page of this section
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight)
          heightLeft -= pageHeight - margin * 2

          // Add additional pages as needed for this section
          while (heightLeft > 0) {
            pageCount++
            position = -pageHeight * pageCount + margin // Position adjustment
            pdf.addPage()
            pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight)
            heightLeft -= pageHeight - margin * 2
          }
        }
      }

      pdf.save("DashboardReport.pdf")
    } catch (error) {
      console.error("PDF export failed:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExport = async () => {
    try {
      setIsExportingExcel(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      const {
        generalStatistic,
        city_ids,
        is_saudi,
        gender,
        ageGroup,
        education_qualification_id,
        dateType,
        dateRange,
      } = filters

      const payload = {
        type:
          generalStatistic === "trial_run_statistics_v2" ||
          generalStatistic === "trial_run_present_statistics" ||
          generalStatistic === "trial_run_present_statistics_v2"
            ? "trial_run_statistics"
            : generalStatistic,
        filters: {
          city_ids: city_ids.map(city => city.value),
          is_saudi,
          gender,
          education_qualification_ids: education_qualification_id.map(
            eduId => eduId.value
          ),
          ageGroup,
          dateFilter: {
            type: dateType,
            start: formatDateForAPI(dateRange.start),
            end: formatDateForAPI(dateRange.end),
          },
        },
      }

      const response = await axios.post(
        `${API_URL}/api/v1/admin/dashboard/export`,
        payload,
        {
          headers: {
            Authorization: token, // Attach token in headers
          },
          responseType: "blob", // Important for file downloads
        }
      )

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        `Trial Operation Dashboard Summary ${today}.xlsx`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExportingExcel(false)
    }
  }

  const tabs = [{ id: "SUMMARY", label: t("Summary") }]

  return (
    <Row className="employee_goal_row">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <div className="d-flex align-items-center gap-2">
            <img width={34} height={34} src={goal16Icon} alt="Goal" />
            <CardTitle
              style={{
                color:
                  layoutMode === layoutModeTypes.LIGHT
                    ? Colors.black
                    : Colors.white,
              }}
              className="m-0"
            >
              {t("Trial Operations Summary View")}
            </CardTitle>
          </div>
          <p
            className="my-2"
            style={{
              color:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.mutedBlue
                  : Colors.white,
            }}
          >
            {t(
              "Summary Statistics on Trial Operations and what has been achieved"
            )}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <IconifyIcon
            icon="bi:filetype-xls"
            width={32}
            height={32}
            style={{
              color:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.primary
                  : Colors.white,
            }}
            onClick={handleExport}
            // isDisabled={isExportingExcel}
          />
          {/*<IconifyIcon*/}
          {/*  icon="solar:file-download-linear"*/}
          {/*  width={32}*/}
          {/*  height={32}*/}
          {/*  onClick={exportAsPDF}*/}
          {/*  style={{*/}
          {/*    color:*/}
          {/*      layoutMode === layoutModeTypes.LIGHT*/}
          {/*        ? Colors.primary*/}
          {/*        : Colors.white,*/}
          {/*  }}*/}
          {/*  isDisabled={isExporting}*/}
          {/*/>*/}
          <PrinterCheck
            onClick={exportAsPDF}
            color={
              layoutMode === layoutModeTypes.LIGHT ? Colors.primary : "#FFFFFF"
            }
            size={"32px"}
            className={"mx-2"}
            absoluteStrokeWidth={true}
            disabled={isExporting}
            cursor={"pointer"}
          />
        </div>
      </div>
      <Col lg={12}>
        <CustomTabs tabs={tabs} activeTab={activeTab} onChange={toggleTab} />
      </Col>
      {activeTab === "SUMMARY" && (
        <Col lg={12} ref={pdfRef}>
          <Card data-page-section="true">
            <CardBody>
              <Table
                responsive
                striped
                style={{ borderRadius: "12px", overflow: "hidden" }}
              >
                <thead
                  style={{
                    backgroundColor:
                      layoutMode === layoutModeTypes.LIGHT
                        ? "rgba(0, 119, 139, 0.16)"
                        : "rgba(0, 139.83, 164.50, 0.16)",
                  }}
                >
                  <tr>
                    {headers.map((header, idx) => (
                      <th key={idx}>
                        <div
                          style={{
                            color:
                              layoutMode === layoutModeTypes.LIGHT
                                ? Colors.primary
                                : "#008CA5",
                            fontSize: "12px",
                          }}
                        >
                          {header.label}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tabularData?.map((job, index) => (
                    <tr key={index}>
                      {headers.map((header, idx) => (
                        <td
                          key={idx}
                          className={header.key === "shift" ? "fw-bold" : ""}
                          style={{
                            color:
                              layoutMode === layoutModeTypes.LIGHT
                                ? header.key === "shift"
                                  ? Colors.primary
                                  : Colors.black
                                : header.key === "shift"
                                ? "#008CA5"
                                : Colors.white,
                          }}
                        >
                          {formatNumber(job[header.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr>
                    <td></td>
                    <td>
                      <strong>{t("Total")}</strong>
                    </td>
                    {headers.slice(2).map((header, idx) => (
                      <td key={idx}>
                        <strong>{formatNumber(totals[header.key])}</strong>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  )
}

export default TrialOperationsGoalsDashboardV2
